import { useState } from 'react';
import { cn } from 'utils/style-utils';

/** The first letter of the first two words, or the first two letters of a single word. */
const getInitials = (name: string) => {
  const words = name.trim().split(/\s+/);
  const initials =
    words.length > 1
      ? `${words[0][0]}${words[1][0]}`
      : words[0].substring(0, 2);
  return initials.toUpperCase();
};

export interface AvatarProps {
  /** Whose avatar it is; the fallback text is made from it. */
  name: string;
  /** The image; without one, or when it fails to load, the fallback text shows. */
  src?: string | null;
  /**
   * What the avatar shows, for screen readers. Leave it out when the name is
   * already written next to the avatar: the avatar is then decorative.
   */
  alt?: string;
  /**
   * The text shown instead of the image. By default the initials of the first
   * two words, or the first two letters of a one-word name.
   */
  fallbackText?: (name: string) => string;
  className?: string;
}

/** A round avatar: an image, or a short text made from the name when there's none. */
const Avatar = ({
  name,
  src,
  alt,
  fallbackText = getInitials,
  className,
}: AvatarProps) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && src !== failedSrc;

  return (
    <div
      aria-hidden={alt ? undefined : true}
      className={cn(
        'size-8 shrink-0 flex items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-primary-100 text-sm text-primary',
        className,
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? ''}
          onError={() => setFailedSrc(src)}
          className="size-full bg-white object-contain"
        />
      ) : (
        <span role={alt ? 'img' : undefined} aria-label={alt}>
          {fallbackText(name)}
        </span>
      )}
    </div>
  );
};

export default Avatar;
